import * as THREE from 'three';
import type { TvShow } from '../content/tv';
import type { SfxName } from '../content/types';
import type { Controls } from '../core/device';
import type { Random } from '../core/Random';
import type { Player } from '../entities/Player';
import type { Vec2 } from '../world/layout';
import type { Stage, StageButton, StageHud, StageInput } from './types';
import { drawIcon } from '../icons/canvas';
import { iconOf } from '../icons/rich';

const LINE_SECONDS = 2.6;
const STATIC_SECONDS = 0.35;

export interface TvSetup {
  /** The TV's glass (a plane whose UVs cover the picture). */
  screen: THREE.Mesh;
  /** Where you sit (world) and which way the couch faces. */
  seat: Vec2;
  facing: number;
  /** A beer in hand while you watch? */
  beer: boolean;
}

/** Wraps a caption into lines that fit `width` pixels. */
function wrap(g: CanvasRenderingContext2D, text: string, width: number): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (g.measureText(next).width > width && line) {
      out.push(line);
      line = word;
    } else line = next;
  }
  if (line) out.push(line);
  return out;
}

/**
 * Watching TV for real: you're on the couch, the show plays on the TV across the room,
 * and the camera sits just behind you. Space flips the channel, E turns it off.
 */
export class TvStage implements Stage {
  readonly title = 'Watching TV';
  readonly crosshair = false;
  readonly pointerLock = false;
  /** Shows you've seen (or flipped past), and whether one was Murray. */
  shows = 0;
  murray = false;
  private t = 0;
  private off = false;
  private show: TvShow;
  private lines: string[] = [];
  private lineAt = 0;
  private staticUntil = 0;
  private readonly canvas = document.createElement('canvas');
  private readonly g: CanvasRenderingContext2D | null;
  private readonly tex: THREE.CanvasTexture;
  private readonly oldMat: THREE.Material | THREE.Material[];
  private mat: THREE.MeshStandardMaterial | null = null;
  private readonly center = new THREE.Vector3();
  private readonly normal = new THREE.Vector3();
  private readonly aspect: number;
  private camera: THREE.PerspectiveCamera | null = null;
  private baseFov = 60;

  constructor(
    private readonly setup: TvSetup,
    private readonly player: Player,
    private readonly pick: () => TvShow,
    private readonly rng: Random,
    private readonly sfx: (n: SfxName) => void,
  ) {
    const p = (setup.screen.geometry as THREE.PlaneGeometry).parameters;
    this.aspect = p.width / p.height;
    this.canvas.height = 300;
    this.canvas.width = Math.round(300 * this.aspect);
    this.g = this.canvas.getContext('2d');
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.oldMat = setup.screen.material;
    this.show = pick();
  }

  get done(): boolean {
    return this.off;
  }

  hint(controls: Controls): string {
    return {
      keys: 'Space flips the channel · E turns it off',
      touch: ':tv: flips the channel · ⏻ turns it off',
      pad: 'A flips the channel · X turns it off',
    }[controls];
  }

  buttons(touch: boolean): StageButton[] {
    return touch
      ? [
          { code: 'Space', label: ':tv:', primary: true },
          { code: 'KeyE', label: '⏻' },
        ]
      : [];
  }

  begin(): void {
    const { player, setup } = this;
    player.teleport(setup.seat.x, setup.seat.z, setup.facing);
    player.scripted = true;
    player.anim.update({ dt: 1, time: 0, speed: 0, pose: setup.beer ? 'sitdrink' : 'sit', seat: 0.5 });
    this.mat = new THREE.MeshStandardMaterial({
      map: this.tex,
      emissive: new THREE.Color('#ffffff'),
      emissiveMap: this.tex,
      emissiveIntensity: 0.95,
      roughness: 0.25,
    });
    setup.screen.material = this.mat;
    this.tune(this.show);
  }

  /** Puts a show on, with a burst of static first. */
  private tune(show: TvShow): void {
    this.show = show;
    this.shows++;
    if (show.id === 'murray') this.murray = true;
    this.lines = show.scenes(this.rng);
    this.lineAt = 0;
    this.staticUntil = this.t + STATIC_SECONDS;
    this.sfx('door');
  }

  update(dt: number, input: StageInput): void {
    this.t += dt;
    this.player.anim.update({
      dt,
      time: this.t,
      speed: 0,
      pose: this.setup.beer ? 'sitdrink' : 'sit',
      seat: 0.5,
    });
    if (input.keys.includes('KeyE') || input.keys.includes('Escape')) {
      this.off = true;
      return;
    }
    const flip =
      input.keys.includes('Space') || input.keys.includes('ArrowRight') || (input.tap && !input.keys.length);
    if (flip) this.tune(this.pick());
    // Captions advance; when the show's over, the next one comes on by itself.
    const line = Math.floor((this.t - this.staticUntil) / LINE_SECONDS);
    if (line > this.lineAt) {
      this.lineAt = line;
      if (this.lineAt === this.lines.length - 1 && this.show.id === 'murray') this.sfx('crowd');
      if (this.lineAt >= this.lines.length + 1) this.tune(this.pick());
    }
    this.draw();
  }

  private draw(): void {
    const g = this.g;
    if (!g) return;
    const W = this.canvas.width;
    const H = this.canvas.height;
    if (this.t < this.staticUntil) {
      // Snow.
      const img = g.createImageData(W, H);
      for (let i = 0; i < img.data.length; i += 4) {
        const v = Math.random() * 255;
        img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
      g.putImageData(img, 0, 0);
      this.tex.needsUpdate = true;
      return;
    }
    const s = this.show;
    // Each channel has its own color, more or less.
    let hash = 0;
    for (const ch of s.id) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
    const hue = Math.abs(hash) % 360;
    const bg = g.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, `hsl(${hue}, 45%, 28%)`);
    bg.addColorStop(1, `hsl(${(hue + 40) % 360}, 50%, 12%)`);
    g.fillStyle = bg;
    g.fillRect(0, 0, W, H);
    // The star of the show, hamming it up.
    const star = iconOf(s.icon);
    if (star) {
      const size = Math.round(H * 0.42);
      g.save();
      g.translate(W / 2, H * 0.47);
      g.rotate(Math.sin(this.t * 3) * 0.08);
      g.scale(1 + Math.sin(this.t * 5) * 0.04, 1 + Math.sin(this.t * 5) * 0.04);
      drawIcon(g, star, -size / 2, -size / 2, size);
      g.restore();
    }
    // Title bar and the channel number.
    g.fillStyle = 'rgba(0,0,0,0.55)';
    g.fillRect(0, 0, W, H * 0.16);
    g.fillStyle = '#ffd400';
    g.font = `${Math.round(H * 0.09)}px Bangers, Impact, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(s.title, W / 2, H * 0.085);
    g.fillStyle = '#7dff7a';
    g.font = `${Math.round(H * 0.075)}px monospace`;
    g.textAlign = 'right';
    g.fillText(s.channel, W - 10, H * 0.24);
    // Captions.
    const caption = this.lines[Math.min(this.lineAt, this.lines.length - 1)] ?? '';
    g.font = `${Math.round(H * 0.062)}px "Comic Neue", "Comic Sans MS", sans-serif`;
    const rows = wrap(g, caption, W * 0.9);
    const rowH = H * 0.07;
    const boxH = rows.length * rowH + H * 0.04;
    g.fillStyle = 'rgba(0,0,0,0.75)';
    g.fillRect(0, H - boxH, W, boxH);
    g.fillStyle = '#ffffff';
    g.textAlign = 'center';
    rows.forEach((r, i) => g.fillText(r, W / 2, H - boxH + H * 0.02 + rowH * (i + 0.5)));
    // Old-TV scanlines.
    g.fillStyle = 'rgba(0,0,0,0.12)';
    for (let y = 0; y < H; y += 3) g.fillRect(0, y, W, 1);
    this.tex.needsUpdate = true;
  }

  frame(camera: THREE.PerspectiveCamera): void {
    const scr = this.setup.screen;
    scr.getWorldPosition(this.center);
    this.normal.set(0, 0, 1).transformDirection(scr.matrixWorld);
    // Up over your shoulder on the couch, high enough that your beer arm isn't in the way.
    const s = this.setup.seat;
    const back = { x: -Math.sin(this.setup.facing), z: -Math.cos(this.setup.facing) };
    const side = { x: -back.z, z: back.x };
    camera.position.set(s.x + back.x * 0.42 + side.x * 0.4, 1.78, s.z + back.z * 0.42 + side.z * 0.4);
    camera.lookAt(this.center.x, this.center.y - 0.1, this.center.z);
    if (this.camera !== camera) {
      this.camera = camera;
      this.baseFov = camera.fov;
      camera.fov = 46;
      camera.updateProjectionMatrix();
    }
  }

  hud(): StageHud {
    return { status: `${this.show.channel} · ${this.show.title}` };
  }

  end(): void {
    if (this.camera) {
      this.camera.fov = this.baseFov;
      this.camera.updateProjectionMatrix();
    }
    this.setup.screen.material = this.oldMat;
    this.mat?.dispose();
    this.tex.dispose();
    const { player, setup } = this;
    player.scripted = false;
    // Up off the couch.
    player.teleport(
      setup.seat.x + Math.sin(setup.facing) * 0.9,
      setup.seat.z + Math.cos(setup.facing) * 0.9,
      setup.facing,
    );
  }
}
